import React from 'react';
import { Briefcase, DollarSign } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { eventsData } from '../../../data/eventsData';

const getStageStyle = (stage: string): string => {
  const styles: Record<string, string> = {
    'ANNOUNCED': 'bg-gray-600 text-white',
    'MOU SIGNED': 'bg-data-amber text-black',
    'NEGOTIATION': 'bg-data-blue text-white',
    'PLANNING': 'bg-cyan-500 text-black',
    'PREPARATION': 'bg-purple-500 text-white',
    'LICENSED': 'bg-data-green/80 text-black',
    'CONSTRUCTION': 'bg-data-green text-black font-bold',
  };
  return styles[stage] || 'bg-gray-600 text-white';
};

const getSectorColor = (sector: string): string => {
  const colors: Record<string, string> = {
    'ENERGY': '#22c55e',
    'FINANCE': '#fbbf24',
    'FINTECH': '#00d4d4',
    'MANUFACTURING': '#00a3ff',
    'INFRASTRUCTURE': '#a855f7',
    'CHEMICALS': '#f59e0b',
  };
  return colors[sector] || '#6b7280';
};

interface DealPipelinePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const DealPipelinePanel: React.FC<DealPipelinePanelProps> = ({ isMinimized, onMinimize }) => {
  const { dealPipeline, summary } = eventsData;

  const totalValue = dealPipeline.reduce((sum, d) => sum + d.value, 0);
  const maxValue = Math.max(...dealPipeline.map(d => d.value));

  // Sort by value descending
  const sortedDeals = [...dealPipeline].sort((a, b) => b.value - a.value);

  // Count by stage
  const constructionDeals = dealPipeline.filter(d => d.stage === 'CONSTRUCTION').length;
  const activeDeals = dealPipeline.filter(d => d.stage !== 'ANNOUNCED').length;

  return (
    <Panel title="DEAL PIPELINE" subtitle="TRANSACTIONS" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero - Pipeline Value */}
        <div className="text-center py-5 px-4 bg-gradient-to-b from-data-green/10 to-transparent rounded-lg border border-data-green/20 mb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Briefcase size={16} className="text-data-green" />
            <span className="text-xs text-data-green uppercase tracking-widest font-semibold">
              Pipeline Value
            </span>
          </div>
          <div className="text-5xl font-mono font-black text-white mb-1">
            <span className="text-3xl">$</span>{(totalValue / 1000).toFixed(1)}<span className="text-3xl">B</span>
          </div>
          <div className="text-xs text-gray-500">{dealPipeline.length} deals tracked</div>
        </div>

        {/* Key Stats Row */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-white">{dealPipeline.length}</div>
            <div className="text-xs text-gray-500">Total Deals</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-data-green/30">
            <div className="text-2xl font-mono font-bold text-data-green">{constructionDeals}</div>
            <div className="text-xs text-gray-500">In Construction</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-amber">{activeDeals}</div>
            <div className="text-xs text-gray-500">Active</div>
          </div>
        </div>

        {/* Deal Cards */}
        <div className="flex-1 overflow-auto custom-scrollbar space-y-2">
          {sortedDeals.slice(0, 5).map((deal, idx) => {
            const widthPct = (deal.value / maxValue) * 100;
            return (
              <div key={idx} className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">{deal.deal}</span>
                  <span className="text-lg font-mono font-black text-data-green">
                    ${(deal.value / 1000).toFixed(1)}B
                  </span>
                </div>

                {/* Value Bar */}
                <div className="h-2 bg-terminal-border rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full bg-data-green rounded-full"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <span style={{ color: getSectorColor(deal.sector) }} className="text-xs font-semibold">
                      {deal.sector}
                    </span>
                    <span className="text-xs text-gray-500">{deal.expected}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${getStageStyle(deal.stage)}`}>
                    {deal.stage}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-3 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default DealPipelinePanel;
